
const get = require('lodash/get');


class LinkedDataImageField {
    get imageUrl(): string {
        return this._imageUrl;
    }

    get licenseText(): string {
        return this._licenseText;
    }

    private _imageUrl: string;
    private _licenseText: string;

    constructor(imageInfo) {
        this._imageUrl    = imageInfo.imageUrl;
        this._licenseText = imageInfo.licenseText;
    }
}

class LinkedDataField {
    get value(): string {
        return this._value;
    }

    get label(): string {
        return this._label;
    }
    private _value: string;
    private _label: string;

    constructor(value, label) {
        this._label = label;
        this._value = value;
    }
}


export class LinkedDataModel {

    private _personId:  string | null;
    private _image:       LinkedDataImageField | null;
    private _personName:  string | null;
    private _description: string | null;
    private _dateOfBirth: LinkedDataField | null;
    private _dateOfDeath: LinkedDataField | null;
    private _fieldOfWork: LinkedDataField | null;
    private _occupation:  LinkedDataField | null;
    private _source:      LinkedDataField | null;


    constructor(response) {
        this.image       = new LinkedDataImageField(this.calculateImageText(response));
        this.personId  = get(response, 'id', null);
        this.personName  = get(response, 'person_name.LC', null);
        this.description = get(response, 'short_description.wikidata', null);
        this.dateOfBirth = this.calculateBirthDeathDate(response, 'birth') ? new LinkedDataField(this.calculateBirthDeathDate(response, 'birth'), 'nui.personEntity.birthDate') : null;
        this.dateOfDeath = this.calculateBirthDeathDate(response, 'death') ? new LinkedDataField(this.calculateBirthDeathDate(response, 'death'), 'nui.personEntity.deathDate') : null;
        this.fieldOfWork = new LinkedDataField(this.calculateOccupationAndWork(response, 'field_of_work'), 'nui.personEntity.fieldOfWork') || null;
        this.occupation  = new LinkedDataField(this.calculateOccupationAndWork(response, 'occupations'), 'nui.personEntity.occupation') || null;
        this.source      = new LinkedDataField('nui.personEntity.sourceText', 'nui.personEntity.source');

    }


    private calculateImageText(response) {

        let imageInfo = {};

        let link_to_image = get(response, 'image_info.link_to_image', '');
        imageInfo['imageUrl'] = link_to_image && ['gif', 'jpg', 'jpeg', 'png'].some(supportedImageFormat => link_to_image.toLowerCase().endsWith(supportedImageFormat)) ? link_to_image : 'img/related_person_image.png';

        if(!link_to_image) {
            return imageInfo;
        }

        let image_name = get(response, 'image_info.image_name', null);
        let link_to_image_page = get(response, 'image_info.link_to_image_page', null);
        let image_artist = get(response, 'image_info.image_artist', null);
        let licenseCode = get(response, 'image_info.licensing.license', null);
        let licenseUrl = this.getLicenseUrl(licenseCode);

        let licenseText = "";
        if(link_to_image_page && image_name) {
            licenseText += '<a target="_blank" href="' + link_to_image_page + '">' + image_name + '</a>';
        }

        if (image_artist && !image_name.includes(image_artist)) {
            licenseText += " by " + image_artist + ".";
        }

        if (licenseCode) {
            licenseText += " This image is licensed under License ";
            licenseText += licenseUrl ? '<a target="_blank" href="' + licenseUrl + '">' + licenseCode + '</a>' : licenseCode;
        }

        imageInfo['licenseText'] = licenseText ? "© " + licenseText : "";

        return imageInfo;

    }

    private calculateOccupationAndWork(response, type) {
        let occupationAndWork = get(response, type + ".LC", null);


        if(occupationAndWork && occupationAndWork.length > 0) {
            return occupationAndWork.slice(0,5).join(", ")
        }
        else {
            let occupationAndWorkWikidata = get(response, type + ".wikidata", null);
            if(!occupationAndWorkWikidata || occupationAndWorkWikidata.length == 0){
                return null;
            }

            // go over the items and put each item under the relevant rank (priority) array.
            // If the rank is other than high/normal put it in 'other' array (also if rank is empty)
            let itemsArrayByRank = {
                high: [],
                normal: [],
                other: []
            };
            let validRankTypes = ['high', 'normal'];

            occupationAndWorkWikidata.forEach(item => {
                let rank = get(item, 'rank', null);
                let rankType = (rank && validRankTypes.indexOf(rank) !== -1) ? rank : 'other';
                itemsArrayByRank[rankType].push(item);
            });

            // return null if none of the arrays have item from some reason
            if(Object.keys(itemsArrayByRank).every(key => itemsArrayByRank[key].length === 0)) {
                return null;
            }

            let occupationAndWorkArray = this.sortOccupationAndWork(itemsArrayByRank);
            //take the 'display' field from each item (ignore empty/missing display fields) and separate with comma.
            let joinedDisplays = occupationAndWorkArray
                .map(item => item.display || '')
                .filter(display => display !== '')
                .join(', ');

            return joinedDisplays;
        }
    }

    private sortOccupationAndWork(itemsArrayByRank) {
        if(!itemsArrayByRank || itemsArrayByRank.length == 0){
            return null;
        }

        let occupationAndWorkArray = [];
        let maxOccupationAndWork = 5;
        let rankTypes = ['high', 'normal', 'other'];

        // going over each priority from 'high' to 'other' till we'll have 'maxOccupationAndWork' results
        rankTypes.forEach(rankType => {
            if(itemsArrayByRank[rankType].length > 0){

                // sort the list by reference - higher references will be first in the array
                itemsArrayByRank[rankType].sort((a, b) => b.references - a.references);
                occupationAndWorkArray.push(...itemsArrayByRank[rankType]);

                // More than maxOccupationAndWork (5) items - need to take the first maxOccupationAndWork (which already sorted).
                if(occupationAndWorkArray.length > maxOccupationAndWork){
                    occupationAndWorkArray = occupationAndWorkArray.slice(0, maxOccupationAndWork);
                    return true; // breaks the loop
                }

                // Exactly 5 items
                if(occupationAndWorkArray.length == maxOccupationAndWork){
                    return true; // breaks the loop
                }
            }
        });

        return occupationAndWorkArray;
    }

    private calculateBirthDeathDate(response, type){
        
        let date = get(response, type + '_date.LC', "").trim() || get(response, type + '_date.wikidata', "").trim();
        let place_LC = get(response, type + '_place.LC', "").trim();

        let birthDeathDateArray = [];
        if(date){
            birthDeathDateArray.push(date);
        }

        if(place_LC) {
            birthDeathDateArray.push(place_LC);
        }
        else {
            let countryWikidata = get(response, type + '_country.wikidata', "").trim();
            let cityWikidataObject = get(response, type + '_city.wikidata', null);
            //birth_city is array while death_city is a string
            if(cityWikidataObject && Array.isArray(cityWikidataObject) && cityWikidataObject.length > 0){
                let trimmedArray = cityWikidataObject.map(str => str.trim());
                let birthDataToPush = countryWikidata ? trimmedArray.join(", ") + " (" + countryWikidata +")" : trimmedArray.join(", ");
                birthDeathDateArray.push(birthDataToPush);
            }
            else if(cityWikidataObject && cityWikidataObject != ""){
                let deathDataToPush = countryWikidata ? cityWikidataObject.trim() + " (" + countryWikidata +")" : cityWikidataObject.trim();
                birthDeathDateArray.push(deathDataToPush);
            }
            else if (countryWikidata) { //in case there is country information without city information
                birthDeathDateArray.push(countryWikidata);
            }
        }

        // comma seperated between each value
        return (birthDeathDateArray && birthDeathDateArray.length > 0) ? birthDeathDateArray.join(", ") : null;
    }

    private getLicenseUrl (licenseCode){
        if(!licenseCode){
            return '';
        }

        const licenses = {
            'CC0':	            'https://creativecommons.org/public-domain/cc0/',
            'CC BY 1.0':	    'https://creativecommons.org/licenses/by/1.0/',
            'CC BY 2.0':	    'https://creativecommons.org/licenses/by/2.0/',
            'CC BY 2.5':	    'https://creativecommons.org/licenses/by/2.5/',
            'CC BY 3.0':	    'https://creativecommons.org/licenses/by/3.0/',
            'CC BY 4.0':	    'https://creativecommons.org/licenses/by/4.0/',
            'CC BY-SA 1.0': 	'https://creativecommons.org/licenses/by-sa/1.0/',
            'CC BY-SA 2.0': 	'https://creativecommons.org/licenses/by-sa/2.0/',
            'CC BY-SA 2.5': 	'https://creativecommons.org/licenses/by-sa/2.5/',
            'CC BY-SA 3.0':	    'https://creativecommons.org/licenses/by-sa/3.0/',
            'CC BY-SA 4.0':	    'https://creativecommons.org/licenses/by-sa/4.0/',
            'Public domain':	'https://creativecommons.org/publicdomain/mark/1.0/',
            'GPLv2':	        'https://www.gnu.org/licenses/old-licenses/gpl-2.0.en.html',
            'LGPL':	            'https://www.gnu.org/licenses/lgpl-3.0.en.html',
            'FAL':	            'https://artlibre.org/licence/lal/en/',
            'LAL':	            'https://artlibre.org/'
        };

        return get(licenses, licenseCode, '');
    }

    get image(): LinkedDataImageField | null {
        return this._image;
    }

    set image(image) {
        return this._image = image;
    }

    get personId(): string | null {
        return this._personId;
    }

    set personId(personId) {
        return this._personId = personId;
    }

    get personName(): string | null {
        return this._personName;
    }

    set personName(personName) {
        return this._personName = personName;
    }

    get description(): string | null {
        return this._description;
    }

    set description(description) {
        return this._description = description;
    }

    get dateOfBirth(): LinkedDataField | null {
        return this._dateOfBirth;
    }

    set dateOfBirth(dateOfBirth) {
        return this._dateOfBirth = dateOfBirth;
    }

    get dateOfDeath(): LinkedDataField | null {
        return this._dateOfDeath;
    }

    set dateOfDeath(dateOfDeath) {
        return this._dateOfDeath = dateOfDeath;
    }

    get fieldOfWork(): LinkedDataField | null {
        return this._fieldOfWork;
    }

    set fieldOfWork(fieldOfWork) {
        return this._fieldOfWork = fieldOfWork;
    }

    get occupation(): LinkedDataField | null {
        return this._occupation;
    }

    set occupation(occupation) {
        return this._occupation = occupation;
    }

    get source(): LinkedDataField | null {
        return this._source;
    }

    set source (source){
        this._source = source;
    }

}



// WEBPACK FOOTER //
// ./src/main/webapp/components/search/fullView/linkedData/linkedDataModel.ts